import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { motion, useScroll, AnimatePresence, type Variants } from "framer-motion";
import Tilt from "react-parallax-tilt";
import { ArrowUpRight, ArrowRight, Layers, BrainCircuit, Cloud, Code2, Blocks, Workflow, Server } from "lucide-react";
// HeroVideo intentionally removed from hero — direct <video> used for reliable switching
import SectionReveal from "@/components/SectionReveal";
import CountUpStat from "@/components/CountUpStat";
import {
  STATS,
  INDUSTRIES,
  CASE_STUDIES,
  TESTIMONIALS,
} from "@/data/site";
import { api } from "@/lib/api";

/** Small navy eyebrow pill */
function Eyebrow({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <div
      className={`flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] font-medium ${
        dark ? "text-[#FFC93A]" : "text-[#6B7280]"
      }`}
    >
      <span className={`w-6 h-px ${dark ? "bg-[#FFC93A]" : "bg-[#0A1628]"}`} />
      {children}
    </div>
  );
}

/** Word-by-word stagger reveal */
const wordContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const wordItem: Variants = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};


/** Rotating hero slides */
type HeroSlide = {
  id: string;
  label: string;         // short tab label
  eyebrow: string;       // eyebrow line
  headline: string;
  accentWord: string;    // single word highlighted yellow, appended after headline
  copy: string;
  bullets: string[];     // 3 capability bullets
  cta: string;
  ctaTo: string;
  videoSrc: string;
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
};

const HERO_SLIDES: HeroSlide[] = [
  {
    id: "consulting",
    label: "SAP Consulting",
    eyebrow: "Technology Consulting · Since 2019",
    headline: "Where SAP meets",
    accentWord: "intelligence.",
    copy: "Enterprise SAP delivery for global system integrators and Fortune-tier companies. BTP, ABAP, CAP, CPI, and S/4 HANA, on full-time payroll.",
    bullets: [
      "SAP BTP, ABAP, CAP, CPI, RAP and S/4 HANA",
      "Staff augmentation for global SAP partners",
      "Deployable in 1-2 weeks, zero ramp-up time",
    ],
    cta: "Explore SAP services",
    ctaTo: "/services/sap",
    videoSrc: "/videos/hero-primary.mp4",
    Icon: Layers,
  },
  {
    id: "ai",
    label: "Applied AI",
    eyebrow: "Applied AI · GenAI Engineering",
    headline: "AI engineered for",
    accentWord: "production.",
    copy: "RAG pipelines, agentic workflows, and LLM-powered copilots. Built on your data, deployed on your cloud, audited for your regulator.",
    bullets: [
      "RAG systems, vector DBs, MLOps and LLMOps",
      "SageMaker, PyTorch, Langchain, CrewAI",
      "AI-native products: AIDIPH, Proctorly, One SAP",
    ],
    cta: "Explore AI services",
    ctaTo: "/services/ai",
    videoSrc: "/videos/hero-2.mp4",
    Icon: BrainCircuit,
  },
  {
    id: "fullstack",
    label: "Full-Stack Dev",
    eyebrow: "Engineering · MERN · Cloud · Mobile",
    headline: "Scalable software,",
    accentWord: "end-to-end.",
    copy: "Full-stack development partnerships across MERN, MEAN, Spring Boot, React Native, Flutter and AWS. Offshore or hybrid, your choice.",
    bullets: [
      "MERN, MEAN, Spring Boot Microservices",
      "React Native, Flutter, AWS, GCP",
      "Open to all collaboration models",
    ],
    cta: "Explore full-stack",
    ctaTo: "/services/fullstack",
    videoSrc: "/videos/hero-3.mp4",
    Icon: Code2,
  },
  {
    id: "products",
    label: "Our Products",
    eyebrow: "In-House Products · AI-native",
    headline: "Products built,",
    accentWord: "not just shipped.",
    copy: "We incubate AI-native products for industries where downtime costs money, lives, or trust. From HCM to track-and-trace to proctoring.",
    bullets: [
      "AIDIPH, AI-driven HCM and payroll platform",
      "IndentLedger, smart hardware supply ledger",
      "Proctorly, One SAP, domain AI SaaS tools",
    ],
    cta: "See all products",
    ctaTo: "/products",
    videoSrc: "/videos/hero-1.mp4",
    Icon: Blocks,
  },
];

function WordReveal({ text, accentIndexes = [] as number[], className = "" }: { text: string; accentIndexes?: number[]; className?: string }) {
  const words = text.split(" ");
  return (
    <motion.span
      variants={wordContainer}
      initial="hidden"
      animate="show"
      className={`inline-block ${className}`}
    >
      {words.map((w, i) => (
        <motion.span key={i} variants={wordItem} className="inline-block mr-[0.25em]">
          {accentIndexes.includes(i) ? (
            <span className="text-[#FFC93A]">{w}</span>
          ) : (
            w
          )}
        </motion.span>
      ))}
    </motion.span>
  );
}

/** Direct video — same pattern as Industries HeroSlider.
 *  `vid.load()` + `vid.play()` on every src change ensures reliable switching. */
function HomeHeroVideo({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const vid = ref.current;
    if (!vid) return;
    vid.load();
    const p = vid.play();
    if (p?.catch) p.catch(() => {});
  }, [src]);
  return (
    <video
      ref={ref}
      key={src}
      muted
      loop
      playsInline
      preload="metadata"
      className="absolute inset-0 w-full h-full object-cover"
      aria-hidden="true"
    >
      <source src={src} type="video/mp4" />
    </video>
  );
}

/* ─── Testimonials ──────────────────────────────────────────────── */
function AuthorAvatar({ name, color }: { name: string; color: string }) {
  const initials = name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center font-display font-extrabold text-[13px] text-[#0A1628] flex-shrink-0"
      style={{ background: color }}
    >
      {initials}
    </div>
  );
}

function TestimonialCard({ t, featured = false }: { t: typeof TESTIMONIALS[0]; featured?: boolean }) {
  return (
    <div
      className={`relative flex flex-col bg-white/[0.04] border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.07] transition-all duration-400 group overflow-hidden ${
        featured ? "md:col-span-2 lg:col-span-1" : ""
      }`}
    >
      {/* Top accent bar */}
      <div className="h-[3px] w-full" style={{ background: t.accentColor }} />

      <div className="flex flex-col flex-1 p-7 lg:p-8">
        {/* Industry tag */}
        <div
          className="text-[9px] uppercase tracking-[0.22em] font-bold mb-5"
          style={{ color: t.accentColor }}
        >
          {t.industry}
        </div>

        {/* Large quote mark */}
        <div
          className="font-display text-[64px] leading-none font-extrabold mb-2 -mt-2 opacity-25 group-hover:opacity-40 transition-opacity"
          style={{ color: t.accentColor }}
        >
          "
        </div>

        {/* Quote */}
        <p className="font-sans text-[15px] text-white/75 leading-relaxed flex-1 group-hover:text-white/90 transition-colors duration-300">
          {t.quote}
        </p>

        {/* Divider */}
        <div className="mt-6 mb-5 h-px bg-white/10" />

        {/* Author row */}
        <div className="flex items-center gap-3">
          <AuthorAvatar name={t.author} color={t.accentColor} />
          <div>
            <div className="font-display font-bold text-sm text-white leading-tight">{t.author}</div>
            <div className="text-[11px] text-white/70 mt-0.5 leading-tight">{t.title} · {t.company}</div>
          </div>
        </div>

        {/* Project tag */}
        <div className="mt-4 flex items-start gap-2">
          <div
            className="w-1 h-1 rounded-full flex-shrink-0 mt-[5px]"
            style={{ background: t.accentColor }}
          />
          <span className="text-[11px] italic leading-snug" style={{ color: t.accentColor + "cc" }}>
            {t.project}
          </span>
        </div>
      </div>
    </div>
  );
}

function TestimonialsSection() {
  const featured = TESTIMONIALS.slice(0, 1)[0];
  const rest = TESTIMONIALS.slice(1);

  return (
    <section className="bg-[#060d1a] text-white py-24 lg:py-32 relative overflow-hidden" data-testid="testimonials">
      {/* Background texture */}
      <div className="absolute inset-0 bg-dot-hero opacity-40 pointer-events-none" />
      {/* Subtle radial accent */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-[#FFC93A]/[0.04] blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

        {/* Header */}
        <SectionReveal className="mb-14 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div className="max-w-2xl">
            <Eyebrow dark>Client voice</Eyebrow>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Real outcomes.{" "}
              <span className="text-[#FFC93A]">Real people.</span>
            </h2>
            <p className="mt-4 text-white/70 text-base leading-relaxed max-w-lg">
              From government platforms in Africa to blockchain infrastructure in India, what our clients say about working with us.
            </p>
          </div>
          {/* aggregate trust signal */}
          <div className="flex items-center gap-8 flex-shrink-0">
            <div className="text-center">
              <div className="font-display text-3xl font-extrabold text-[#FFC93A]">5★</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Average rating</div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="text-center">
              <div className="font-display text-3xl font-extrabold text-[#FFC93A]">100%</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Repeat clients</div>
            </div>
          </div>
        </SectionReveal>

        {/* Featured large card + first two side cards */}
        <SectionReveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* Featured, spans 2 cols */}
            <div className="lg:col-span-2 relative flex flex-col bg-white/[0.05] border border-white/[0.1] hover:border-[#FFC93A]/30 hover:bg-white/[0.08] transition-all duration-400 group overflow-hidden">
              <div className="h-[3px] w-full" style={{ background: featured.accentColor }} />
              <div className="flex flex-col flex-1 p-8 lg:p-10">
                <div className="text-[9px] uppercase tracking-[0.22em] font-bold mb-4" style={{ color: featured.accentColor }}>
                  {featured.industry}
                </div>
                <div className="font-display text-[80px] leading-none font-extrabold -mt-3 mb-1 opacity-20 group-hover:opacity-35 transition-opacity" style={{ color: featured.accentColor }}>"</div>
                <p className="font-sans text-base sm:text-lg text-white/80 leading-relaxed flex-1 group-hover:text-white/95 transition-colors max-w-2xl">
                  {featured.quote}
                </p>
                <div className="mt-8 mb-6 h-px bg-white/10" />
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <AuthorAvatar name={featured.author} color={featured.accentColor} />
                    <div>
                      <div className="font-display font-bold text-sm text-white">{featured.author}</div>
                      <div className="text-[11px] text-white/70 mt-0.5">{featured.title} · {featured.company}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: featured.accentColor }} />
                    <span className="text-[11px] italic" style={{ color: featured.accentColor + "cc" }}>{featured.project}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* First side card */}
            <TestimonialCard t={rest[0]} />
          </div>

          {/* Bottom row, remaining cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rest.slice(1).map((t, i) => (
              <TestimonialCard key={i} t={t} />
            ))}
          </div>
        </SectionReveal>
      </div>
    </section>
  );
}

/* ─── Client data with type labels + domains for logo lookup ────── */
type ClientEntry = { name: string; type: string; domain: string; logo?: string };

// logo paths are relative to /public, add more as files are provided
const CLIENT_ROWS: ClientEntry[][] = [
  [
    { name: "SAP Labs India",           type: "System Integrator", domain: "sap.com",              logo: "/videos/thumbs/SAP-Logo.svg.png" },
    { name: "NTT Data",                 type: "System Integrator", domain: "nttdata.com",           logo: "/videos/thumbs/ntt.png" },
    { name: "FSoft / FPT Software",     type: "Technology",        domain: "fpt.com",               logo: "/videos/thumbs/fpt.png" },
    { name: "Bahwan CyberTek",          type: "Technology",        domain: "bahwancybertek.com",    logo: "/videos/thumbs/bhavan-cybertech.png" },
    { name: "Deviare Africa",           type: "System Integrator", domain: "deviare.africa",        logo: "/videos/thumbs/deviare.png" },
    { name: "Nu10 Technologies",        type: "Technology",        domain: "nu10.io",               logo: "/videos/thumbs/NU10-Logo.png" },
    { name: "Rejolut Technologies",     type: "Blockchain",        domain: "rejolut.com",           logo: "/videos/thumbs/rejolut.png" },
    { name: "SAP Labs Bulgaria",        type: "System Integrator", domain: "sap.com",               logo: "/videos/thumbs/SAP-Logo.svg.png" },
    { name: "Birlasoft",                type: "System Integrator", domain: "birlasoft.com",         logo: "/videos/thumbs/birlasoft.png" },
    { name: "HCL Technologies",         type: "System Integrator", domain: "hcltech.com",            logo: "/videos/thumbs/hcl.png" },
    { name: "Toro Cloud",               type: "Technology",        domain: "torocloud.com",         logo: "/videos/thumbs/toro.png" },
  ],
  [
    { name: "Inq Africa",               type: "Enterprise",        domain: "inq.africa",            logo: "/videos/thumbs/inq.png" },
    { name: "Educurve India",           type: "EdTech",            domain: "educurve.in",           logo: "/videos/thumbs/educurve.png" },
    { name: "Usterra",                  type: "Product",           domain: "usterra.com",           logo: "/videos/thumbs/susterra.png" },
    { name: "mTilda",                   type: "Product",           domain: "mtilda.com",            logo: "/videos/thumbs/mtilda.png" },
    { name: "IASA",                     type: "Enterprise",        domain: "iasaglobal.org",        logo: "/videos/thumbs/iasa.png" },
    { name: "Mech Solutions Ltd",       type: "Enterprise",        domain: "mechsolutions.co.uk",   logo: "/videos/thumbs/mech.png" },
    { name: "MiDS",                     type: "Technology",        domain: "mids.com",              logo: "/videos/thumbs/mids.png" },
    { name: "Book Einstein",            type: "EdTech",            domain: "bookeinstein.com",      logo: "/videos/thumbs/book-einstein.png" },
    { name: "CheckMark Cyber Botswana", type: "Enterprise",        domain: "checkmarkcyber.bw",     logo: "/videos/thumbs/checkmark.png" },
    { name: "easyReg",                  type: "Regulatory",        domain: "easyreg.co.za",         logo: "/videos/thumbs/easyreg.png" },
    { name: "Best Apps",                type: "Technology",        domain: "bestapps.com",           logo: "/videos/thumbs/bestapps.png" },
    { name: "ITC Infotech",             type: "System Integrator", domain: "itcinfotech.com",        logo: "/videos/thumbs/itc-infotech.png" },
  ],
];

const TYPE_ACCENT: Record<string, string> = {
  "System Integrator": "#FFC93A",
  "Technology":        "#60A5FA",
  "Enterprise":        "#34D399",
  "EdTech":            "#F87171",
  "Blockchain":        "#A78BFA",
  "Product":           "#FB923C",
  "Regulatory":        "#94A3B8",
};

function ClientLogo({ name, domain, accent, logo }: { name: string; domain: string; accent: string; logo?: string }) {
  const [srcIndex, setSrcIndex] = React.useState(0);
  const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  // Priority: local file → Google favicon → initials fallback
  const sources = [
    ...(logo ? [logo] : []),
    `https://www.google.com/s2/favicons?sz=64&domain_url=https://${domain}`,
  ];

  if (srcIndex >= sources.length) {
    return (
      <div
        className="w-20 h-8 rounded flex items-center justify-center text-[10px] font-extrabold text-white/70 flex-shrink-0"
        style={{ background: "transparent", letterSpacing: "0.05em" }}
      >
        {initials}
      </div>
    );
  }

  return (
    <div className="w-20 h-9 flex items-center justify-center flex-shrink-0 overflow-hidden">
      <img
        src={sources[srcIndex]}
        alt={name}
        className="max-w-full max-h-full object-contain"
        onError={() => setSrcIndex((i) => i + 1)}
        loading="lazy"
      />
    </div>
  );
}

function ClientPill({ name, type, domain, logo }: ClientEntry) {
  const accent = TYPE_ACCENT[type] ?? "#E8A911";
  return (
    <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 rounded-sm border border-[#E8A911]/40 bg-white hover:border-[#E8A911]/80 hover:bg-[#fffdf2] transition-all duration-300 cursor-default mx-2.5 shadow-sm">
      <ClientLogo name={name} domain={domain} accent={accent} logo={logo} />
      <div>
        <div className="font-display font-bold text-sm text-[#0A1628] whitespace-nowrap">{name}</div>
        <div className="text-[9px] uppercase tracking-[0.18em] mt-0.5 whitespace-nowrap" style={{ color: accent }}>{type}</div>
      </div>
    </div>
  );
}

function MarqueeRow({ items, reverse = false }: { items: ClientEntry[]; reverse?: boolean }) {
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden w-full py-2 group-marquee">
      <div
        className={`flex w-max ${reverse ? "animate-marquee-reverse" : "animate-marquee"}`}
        style={{ willChange: "transform" }}
      >
        {doubled.map((c, i) => (
          <ClientPill key={`${c.name}-${i}`} name={c.name} type={c.type} domain={c.domain} logo={c.logo} />
        ))}
      </div>
    </div>
  );
}

function ClientsSection() {
  return (
    <section className="bg-white py-20 lg:py-28 overflow-hidden relative border-y border-[#0A1628]/08" data-testid="clients-strip">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mb-14">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          {/* Left: headline */}
          <SectionReveal>
            <Eyebrow>Client Portfolio</Eyebrow>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight max-w-xl text-[#0A1628]">
              Trusted by{" "}
              <span className="text-[#E8A911]">innovators, integrators</span>
              {" "}& enterprises.
            </h2>
            <p className="mt-4 text-[#1F2228]/75 text-base leading-relaxed max-w-lg">
              From global system integrators and Fortune-tier enterprises to high-growth tech startups, spanning 10+ countries across 6 industry verticals.
            </p>
          </SectionReveal>

          {/* Right: stat blocks */}
          <SectionReveal className="grid grid-cols-3 gap-5 lg:gap-8 flex-shrink-0">
            {[
              { value: "20+", label: "Active clients" },
              { value: "10+", label: "Countries" },
              { value: "6",   label: "Industry verticals" },
            ].map((s) => (
              <div key={s.label} className="text-center lg:text-right">
                <div className="font-display text-3xl sm:text-4xl font-extrabold text-[#E8A911] leading-none">{s.value}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0A1628]/40 mt-1.5 whitespace-nowrap">{s.label}</div>
              </div>
            ))}
          </SectionReveal>
        </div>
      </div>

      {/* Marquee rows */}
      <div className="relative space-y-3">
        <MarqueeRow items={CLIENT_ROWS[0]} />
        <MarqueeRow items={CLIENT_ROWS[1]} reverse />
      </div>
    </section>
  );
}

export default function Home() {
  const [posts, setPosts] = useState<any[]>([]);
  const [slideIdx, setSlideIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  const [tick, setTick] = useState(0);
  const heroRef = useRef<HTMLElement>(null);

  const goToSlide = (i: number) => {
    if (i === slideIdx) return;
    setDirection(i > slideIdx ? 1 : -1);
    setSlideIdx(i);
    setTick((t) => t + 1);
  };

  useEffect(() => {
    api.get("/blog/posts").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => setPosts([]));
  }, []);

  // Simple auto-advance — always running, resets when user manually clicks a tab
  useEffect(() => {
    const t = setInterval(() => {
      setDirection(1);
      setSlideIdx((i) => (i + 1) % HERO_SLIDES.length);
    }, 12000);
    return () => clearInterval(t);
  }, [tick]);

  const slide = HERO_SLIDES[slideIdx];

  return (
    <div data-testid="home-page" className="bg-white">
      <SEO
        title="KIBA Labs | SAP Consulting, AI & Full-Stack Engineering"
        description="KIBA Labs delivers enterprise-grade SAP BTP, Applied AI, and full-stack engineering. Pre-trained, payroll-employed bench. Deploy in 1-2 weeks. Trusted by global system integrators."
        path="/"
      />
      {/* ─────────── HERO ─────────── */}
      <section
        ref={heroRef}
        className="relative bg-[#0A1628] text-white overflow-hidden -mt-[72px] min-h-screen flex flex-col"
        data-testid="hero-section"
      >
        {/* Video — direct element, vid.load()+play() on every src change = reliable */}
        <AnimatePresence mode="sync">
          <motion.div
            key={`video-${slide.videoSrc}`}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <HomeHeroVideo src={slide.videoSrc} />
          </motion.div>
        </AnimatePresence>

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/50 to-[#0A1628]/15 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/80 via-[#0A1628]/30 to-transparent pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-[#0A1628]/70 to-transparent pointer-events-none" />

        {/* Yellow progress bar */}
        <motion.div
          key={`progress-${slideIdx}-${tick}`}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 10, ease: "linear" }}
          className="absolute top-0 left-0 right-0 h-[3px] bg-[#FFC93A] origin-left z-20"
        />

        {/* Ghost slide number — top right */}
          <AnimatePresence mode="wait">
            <motion.div
            key={`ghost-${slideIdx}`}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute top-[18%] right-8 lg:right-16 pointer-events-none select-none z-10"
            aria-hidden="true"
          >
            <span className="font-display font-extrabold text-[clamp(120px,18vw,240px)] leading-none text-white/[0.05] tracking-tightest">
              {String(slideIdx + 1).padStart(2, "0")}
            </span>
          </motion.div>
        </AnimatePresence>

        {/* ── Main content — bottom-left, like Industries ── */}
        <div className="relative flex-1 flex flex-col justify-end pb-0 pt-[72px] px-6 sm:px-10 lg:px-16 xl:px-20">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={`content-${slide.id}`}
              custom={direction}
              variants={{
                enter: (dir: number) => ({}),
                center: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
                exit: {},
              }}
              initial="enter"
              animate="center"
              exit="exit"
              className="pb-10 space-y-5 max-w-2xl"
            >
              {/* Eyebrow */}
              <motion.div
                custom={direction}
                variants={{
                  enter: (dir: number) => ({ y: dir > 0 ? 24 : -24, opacity: 0 }),
                  center: { y: 0, opacity: 1, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
                  exit: { opacity: 0, transition: { duration: 0.2 } },
                }}
                className="flex items-center gap-3 text-[11px] uppercase tracking-[0.26em] text-[#FFC93A] font-semibold"
              >
                <span className="w-5 h-px bg-[#FFC93A]" />
                {slide.eyebrow}
              </motion.div>

              {/* Icon + Headline row */}
              <motion.div
                custom={direction}
                variants={{
                  enter: (dir: number) => ({ y: dir > 0 ? 30 : -30, opacity: 0 }),
                  center: { y: 0, opacity: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
                  exit: { opacity: 0, transition: { duration: 0.25 } },
                }}
                className="flex items-start gap-5"
              >
                <div className="mt-2 shrink-0 w-10 h-10 rounded-full bg-[#FFC93A]/15 border border-[#FFC93A]/25 flex items-center justify-center">
                  <slide.Icon className="w-5 h-5 text-[#FFC93A]" strokeWidth={1.4} />
                </div>
                <h1
                  className="font-display text-[clamp(2.4rem,5.5vw,4.8rem)] font-extrabold tracking-tightest leading-[1.02] text-white"
                data-testid="hero-headline"
              >
                  {slide.headline}{" "}
                  <span className="text-[#FFC93A]">{slide.accentWord}</span>
              </h1>
              </motion.div>

              {/* Yellow divider */}
              <motion.div
                custom={direction}
                variants={{
                  enter: (dir: number) => ({ scaleX: 0, opacity: 0 }),
                  center: { scaleX: 1, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } },
                  exit: { opacity: 0, transition: { duration: 0.15 } },
                }}
                style={{ originX: 0 }}
              >
                <div className="w-12 h-[3px] bg-[#FFC93A]" />
              </motion.div>

              {/* Copy */}
              <motion.p
                custom={direction}
                variants={{
                  enter: (dir: number) => ({ y: dir > 0 ? 20 : -20, opacity: 0 }),
                  center: { y: 0, opacity: 1, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
                  exit: { opacity: 0, transition: { duration: 0.2 } },
                }}
                className="text-[15px] lg:text-base text-white/80 leading-[1.75] max-w-lg"
                data-testid="hero-copy"
              >
                {slide.copy}
              </motion.p>

              {/* Capability bullets */}
              <motion.ul
                custom={direction}
                variants={{
                  enter: (dir: number) => ({ y: dir > 0 ? 16 : -16, opacity: 0 }),
                  center: { y: 0, opacity: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
                  exit: { opacity: 0, transition: { duration: 0.18 } },
                }}
                className="space-y-1.5"
              >
                {slide.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-white/75 text-[13px] leading-snug">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FFC93A] mt-[5px] flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </motion.ul>

              {/* CTAs */}
          <motion.div
                custom={direction}
                variants={{
                  enter: (dir: number) => ({ y: dir > 0 ? 16 : -16, opacity: 0 }),
                  center: { y: 0, opacity: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
                  exit: { opacity: 0, transition: { duration: 0.18 } },
                }}
                className="flex items-center gap-5 pt-2"
          >
            <Link
                  to={slide.ctaTo}
                  className="group inline-flex items-center gap-2 bg-[#FFC93A] text-[#0A1628] px-6 py-3.5 font-bold text-[11px] uppercase tracking-[0.18em] hover:bg-white transition-colors"
              data-testid="hero-cta-primary"
            >
                  {slide.cta} <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/partner"
                  className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/75 hover:text-[#FFC93A] transition-colors font-semibold"
              data-testid="hero-cta-secondary"
            >
              Partner With Us <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── Bottom navigation strip — full-width tab row ── */}
        <div className="relative z-20 border-t border-white/10 bg-[#0A1628]/80 backdrop-blur-sm">
          <div className="flex divide-x divide-white/10 overflow-x-auto">
            {HERO_SLIDES.map((s, i) => {
              const SIcon = s.Icon;
              const isActive = i === slideIdx;
              return (
                <button
                  key={s.id}
                  onClick={() => goToSlide(i)}
                  data-testid={`hero-rail-${s.id}`}
                  className={`flex-1 min-w-[130px] flex items-center gap-3 px-5 py-4 transition-colors relative group ${
                    isActive ? "bg-[#FFC93A]/10" : "hover:bg-white/5"
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="hero-active-tab"
                      className="absolute top-0 left-0 right-0 h-[2px] bg-[#FFC93A]"
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                  <SIcon
                    className={`w-4 h-4 shrink-0 transition-colors ${isActive ? "text-[#FFC93A]" : "text-white/30 group-hover:text-white/75"}`}
                    strokeWidth={1.5}
                  />
                  <span
                    className={`text-[10px] uppercase tracking-[0.18em] font-semibold leading-tight text-left transition-colors ${
                      isActive ? "text-[#FFC93A]" : "text-white/40 group-hover:text-white/80"
                    }`}
                  >
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────── STATS (navy continues, dark band) ─────────── */}
      <section className="bg-[#0A1628] text-white py-20 lg:py-24 border-t border-white/5" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionReveal className="mb-14">
            <Eyebrow dark>By the numbers</Eyebrow>
          </SectionReveal>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-y-10 gap-x-8">
            {STATS.map((s, i) => (
              <SectionReveal key={s.label} delay={i * 0.08}>
                <CountUpStat
                  value={s.value}
                  suffix={s.suffix}
                  label={s.label}
                  dark
                  testId={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                />
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── WHAT WE DO, Sticky header + vertical snap cards ─────────── */}
      <WhatWeDo />

      {/* ─────────── INDUSTRIES, Accordion stretch ─────────── */}
      <IndustriesAccordionSection />

      {/* ─────────── CAPABILITY HIGHLIGHTS ─────────── */}
      <CapabilityHighlights />

      {/* ─────────── CLIENTS ─────────── */}
      <ClientsSection />

      {/* ─────────── TESTIMONIALS ─────────── */}
      <TestimonialsSection />

      {/* ─────────── INSIGHTS / BLOG PREVIEW ─────────── */}
      <section className="bg-[#f5f6f8] py-24 lg:py-32 relative overflow-hidden" data-testid="blog-preview">
        {/* Subtle decorative accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#E8A911]/60 to-transparent" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          {/* Header row */}
          <SectionReveal className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <Eyebrow>Insights</Eyebrow>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-[#0A1628]">
                Field notes from SAP,{" "}
                <span className="text-[#E8A911]">Cloud & AI</span> delivery.
              </h2>
            </div>
                  <Link
              to="/blog"
              className="group inline-flex items-center gap-2 text-sm font-bold text-[#0A1628] border border-[#0A1628]/20 px-5 py-2.5 hover:border-[#E8A911] hover:text-[#E8A911] transition-all duration-200 flex-shrink-0"
            >
              All insights <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
              </SectionReveal>

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <SectionReveal key={p.id} delay={i * 0.12}>
                <Link
                  to={`/blog/${p.slug}`}
                  className="group flex flex-col h-full bg-white border border-[#0A1628]/08 hover:border-[#E8A911]/60 hover:shadow-lg transition-all duration-300"
                  data-testid={`blog-preview-${p.slug}`}
                >
                  {/* Coloured top bar */}
                  <div
                    className="h-1 w-full transition-all duration-300"
                    style={{ background: i === 0 ? "#E8A911" : i === 1 ? "#0A1628" : "#4A90D9" }}
                  />
                  <div className="flex flex-col flex-1 p-7">
                    {/* Category + read time */}
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E8A911] bg-[#E8A911]/10 px-2.5 py-1 rounded-sm">
                        {p.category}
                      </span>
                      <span className="text-[11px] text-[#0A1628]/40 font-medium">{p.reading_time} min read</span>
                  </div>

                    {/* Title */}
                    <h3 className="font-display text-lg sm:text-xl font-extrabold leading-snug text-[#0A1628] group-hover:text-[#E8A911] transition-colors flex-1">
                    {p.title}
                  </h3>

                    {/* Excerpt */}
                    <p className="mt-4 text-sm text-[#1F2228]/75 leading-relaxed line-clamp-3">{p.excerpt}</p>

                    {/* CTA */}
                    <div className="mt-7 pt-5 border-t border-[#0A1628]/08 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1628] group-hover:text-[#E8A911] transition-colors">
                        Read article
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                      <div className="w-8 h-8 rounded-full bg-[#0A1628]/05 group-hover:bg-[#E8A911]/15 flex items-center justify-center transition-colors">
                        <ArrowUpRight className="w-3.5 h-3.5 text-[#0A1628]/40 group-hover:text-[#E8A911] transition-colors" />
                      </div>
                    </div>
                  </div>
                </Link>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FINAL CTA, Navy ─────────── */}
      <section className="bg-[#0A1628] text-white py-28 lg:py-36 relative overflow-hidden" data-testid="final-cta">
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <SectionReveal>
            <Eyebrow dark>Let's work together</Eyebrow>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Ready to scale your SAP or technology team?
            </h2>
            <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Pre-vetted, payroll-employed engineers. Onboarded in 1–2 weeks. Built for system integrators and enterprise teams that move fast.
            </p>
            <div className="mt-12">
              <Link
                to="/partner"
                className="inline-flex items-center gap-2 bg-[#FFC93A] text-[#0A1628] px-8 py-4 font-bold rounded-none text-[13px] uppercase tracking-wide hover:bg-white transition-colors"
                data-testid="final-cta-button"
              >
                Partner with us <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}

/* ─────────── Capability cards data ─────────── */
type CapabilityCard = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  tags: string[];
  Icon: typeof Layers;
  accent: string; // yellow accent label
};

const CAPABILITY_CARDS: CapabilityCard[] = [
  {
    id: "sap",
    eyebrow: "Enterprise ERP",
    title: "SAP Engineering",
    description:
      "Full-spectrum SAP delivery, BTP, CAP, ABAP, Fiori, CPI, and S/4HANA. From greenfield implementations to side-by-side extensions and cloud migrations, we cover every layer of the SAP stack.",
    tags: ["SAP BTP", "CAP (Node / Java)", "ABAP & RAP", "SAP Fiori / UI5", "S/4HANA", "CPI & Integration"],
    Icon: Layers,
    accent: "SAP Vertical",
  },
  {
    id: "ai",
    eyebrow: "Applied Intelligence",
    title: "AI & GenAI Engineering",
    description:
      "Production-grade AI, not demos. We build RAG pipelines, agentic workflows, LLM copilots, MLOps platforms, and domain-specific models wired into enterprise systems and regulated environments.",
    tags: ["RAG & Vector Search", "LLM / Agents", "MLOps & LLMOps", "SAP AI Core", "AWS Sagemaker", "Document AI"],
    Icon: BrainCircuit,
    accent: "AI Vertical",
  },
  {
    id: "cloud",
    eyebrow: "Infrastructure & DevOps",
    title: "Cloud Engineering",
    description:
      "AWS and Azure landing zones, multi-region architectures, container platforms, CI/CD pipelines, and infrastructure-as-code. We build production-ready cloud foundations that scale without surprises.",
    tags: ["AWS / Azure", "Terraform / IaC", "Kubernetes", "GitHub Actions", "Observability", "Cost Engineering"],
    Icon: Cloud,
    accent: "Cloud Vertical",
  },
  {
    id: "fullstack",
    eyebrow: "Any Stack, Any Language",
    title: "Full-Stack & Microservices",
    description:
      "MERN, MEAN, Java, Go, Python, .NET, we are language and framework agnostic. API-first microservices, real-time systems, mobile applications, and headless front-ends engineered for production at scale.",
    tags: ["MERN / MEAN", "Java + Spring Boot", "Go / Python / .NET", "REST & GraphQL APIs", "React / Next.js", "React Native"],
    Icon: Code2,
    accent: "Full-Stack",
  },
  {
    id: "blockchain",
    eyebrow: "Web3 & DeFi",
    title: "Blockchain & Smart Contracts",
    description:
      "On-chain architecture, Solidity smart contracts, NFT platforms, and DeFi protocols, all built audit-ready from day one. We deliver from protocol design through mainnet deployment.",
    tags: ["Solidity", "EVM / Hardhat", "NFT Marketplaces", "DeFi Protocols", "IPFS / Filecoin", "Audit Prep"],
    Icon: Blocks,
    accent: "Web3 Vertical",
  },
  {
    id: "staff-aug",
    eyebrow: "Engagement Model",
    title: "Staff Augmentation",
    description:
      "Pre-vetted, payroll-employed engineers embedded directly into your delivery team. You own the direction, we own hiring, HR, and compliance. One engineer in 2–3 days. A team of 3–5 within a week.",
    tags: ["On our payroll", "No hiring overhead", "1 engineer in 2–3 days", "Team of 3–5 in <5 days", "Tech Lead included", "Flexible duration"],
    Icon: Workflow,
    accent: "How We Engage",
  },
  {
    id: "offshore",
    eyebrow: "Engagement Model",
    title: "Offshore Delivery & BOT",
    description:
      "End-to-end project ownership across architecture, build, QA, and deployment. Fixed Bid, T&M, or Build-Operate-Transfer models. Dedicated POD with measurable milestones and 85%+ code coverage.",
    tags: ["Fixed Bid / T&M / BOT", "Full-cycle ownership", "Dedicated POD", "85%+ code coverage", "Agile sprint governance", "Global delivery"],
    Icon: Server,
    accent: "How We Engage",
  },
];

/* ─────────── Per-card architectural visualisations ─────────── */
function FlowNode({ label, sub, yellow }: { label: string; sub?: string; yellow?: boolean }) {
  return (
    <div className={`px-4 py-3 border text-center min-w-[110px] ${yellow ? "border-[#FFC93A] bg-[#FFC93A]" : "border-white/20 bg-white/[0.04]"}`}>
      <div className={`text-[11px] font-bold uppercase tracking-wide ${yellow ? "text-[#0A1628]" : "text-white/80"}`}>{label}</div>
      {sub && <div className={`text-[9px] mt-0.5 uppercase tracking-wider ${yellow ? "text-[#0A1628]/60" : "text-white/40"}`}>{sub}</div>}
    </div>
  );
}
function Arrow({ vertical }: { vertical?: boolean }) {
  return vertical
    ? <div className="flex justify-center py-1"><div className="w-px h-5 bg-[#FFC93A]/40 relative"><div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-l-transparent border-r-transparent border-t-[#FFC93A]/40" /></div></div>
    : <div className="flex items-center px-1"><div className="flex-1 h-px bg-[#FFC93A]/40 relative"><div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[4px] border-b-[4px] border-l-[5px] border-t-transparent border-b-transparent border-l-[#FFC93A]/40" /></div></div>;
}

function CardVisual({ id, accent }: { id: string; accent: string }) {
  const visuals: Record<string, React.ReactNode> = {
    sap: (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0 px-8">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFC93A] font-bold mb-5">SAP Architecture Stack</div>
        <div className="flex items-center gap-1 w-full justify-center">
          <FlowNode label="S/4HANA" sub="ERP Core" />
          <Arrow />
          <FlowNode label="BTP" sub="Platform" yellow />
          <Arrow />
          <FlowNode label="CAP / RAP" sub="App Layer" />
        </div>
        <Arrow vertical />
        <div className="flex items-center gap-1 w-full justify-center">
          <FlowNode label="CPI" sub="Integration" />
          <Arrow />
          <FlowNode label="Fiori / UI5" sub="UX Layer" yellow />
          <Arrow />
          <FlowNode label="SAP AI Core" sub="Intelligence" />
        </div>
      </div>
    ),
    ai: (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0 px-10">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFC93A] font-bold mb-5">GenAI Pipeline</div>
        <div className="flex flex-col items-center w-full gap-0">
          <FlowNode label="Your Data" sub="Docs · DB · API" />
          <Arrow vertical />
          <FlowNode label="RAG Engine" sub="Embed · Chunk · Retrieve" yellow />
          <Arrow vertical />
          <FlowNode label="LLM / Agent" sub="GPT-4 · Llama · Claude" />
          <Arrow vertical />
          <FlowNode label="Grounded Response" sub="Auditable · Accurate" yellow />
        </div>
      </div>
    ),
    cloud: (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-8 gap-3">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFC93A] font-bold">Cloud Foundation</div>
        <div className="w-full border border-[#FFC93A]/30 px-4 py-2 text-center">
          <div className="text-[11px] font-bold text-[#FFC93A] uppercase tracking-wide">Multi-Cloud Landing Zone</div>
          <div className="text-[9px] text-white/35 mt-0.5">AWS · Azure · GCP</div>
        </div>
        <div className="grid grid-cols-3 gap-2 w-full">
          {["Networking\n& IAM", "Container\nPlatform\n(K8s)", "CI/CD\nPipelines"].map((t) => (
            <div key={t} className="border border-white/15 bg-white/[0.03] px-2 py-3 text-center">
              <div className="text-[10px] text-white/70 font-medium whitespace-pre-line leading-tight">{t}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 w-full">
          {["Observability\n& Alerts", "IaC\n(Terraform)"].map((t) => (
            <div key={t} className="border border-white/10 bg-white/[0.02] px-2 py-2 text-center">
              <div className="text-[10px] text-white/70 whitespace-pre-line leading-tight">{t}</div>
            </div>
          ))}
        </div>
      </div>
    ),
    fullstack: (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-8 gap-0">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFC93A] font-bold mb-4">Full-Stack Layer Model</div>
        {[
          { label: "Frontend", sub: "React · Next.js · React Native", w: "w-full", yellow: false },
          { label: "API Gateway", sub: "REST · GraphQL · gRPC", w: "w-[88%]", yellow: true },
          { label: "Microservices", sub: "Java · Node.js · Go · Python · .NET", w: "w-[76%]", yellow: false },
          { label: "Data Layer", sub: "PostgreSQL · MongoDB · Redis · Kafka", w: "w-[64%]", yellow: false },
          { label: "Cloud Infra", sub: "AWS · Azure · Docker · K8s", w: "w-[52%]", yellow: false },
        ].map((row) => (
          <div key={row.label} className={`${row.w} border ${row.yellow ? "border-[#FFC93A]/60 bg-[#FFC93A]/8" : "border-white/15 bg-white/[0.03]"} px-3 py-2 mb-1.5 flex items-center justify-between`}>
            <span className={`text-[11px] font-bold uppercase tracking-wide ${row.yellow ? "text-[#FFC93A]" : "text-white/75"}`}>{row.label}</span>
            <span className="text-[9px] text-white/35">{row.sub}</span>
          </div>
        ))}
      </div>
    ),
    blockchain: (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-8 gap-0">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFC93A] font-bold mb-5">Deployment Lifecycle</div>
        <div className="flex items-start gap-1 w-full justify-center">
          {[
            { label: "Design", sub: "Protocol\n& Tokenomics", step: "01" },
            { label: "Build", sub: "Solidity\nContracts", step: "02", yellow: true },
            { label: "Audit", sub: "Slither\n& Manual", step: "03" },
            { label: "Deploy", sub: "Mainnet\nLaunch", step: "04", yellow: true },
          ].map((s, i, arr) => (
            <React.Fragment key={s.label}>
              <div className={`flex flex-col items-center gap-1.5 min-w-[72px] ${s.yellow ? "" : ""}`}>
                <div className={`text-[9px] font-mono text-[#FFC93A]/50`}>{s.step}</div>
                <div className={`w-10 h-10 border flex items-center justify-center text-[10px] font-bold uppercase tracking-wide ${s.yellow ? "border-[#FFC93A] text-[#FFC93A] bg-[#FFC93A]/10" : "border-white/20 text-white/75"}`}>
                  {s.label.slice(0, 3)}
                </div>
                <div className="text-[8px] text-white/35 text-center whitespace-pre-line leading-snug">{s.sub}</div>
              </div>
              {i < arr.length - 1 && <div className="flex items-center mt-5 mx-0.5"><div className="w-6 h-px bg-[#FFC93A]/30" /></div>}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-6 w-full border-t border-white/10 pt-4 grid grid-cols-3 gap-2">
          {["EVM · Hardhat", "IPFS · Filecoin", "DeFi · NFT"].map((t) => (
            <div key={t} className="text-center text-[9px] text-white/40 uppercase tracking-wide">{t}</div>
          ))}
        </div>
      </div>
    ),
    "staff-aug": (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-8 gap-4">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFC93A] font-bold">How Your Team Expands</div>
        <div className="w-full flex gap-3 justify-center">
          <div className="border border-[#FFC93A]/40 bg-[#FFC93A]/5 px-4 py-3 text-center flex-1">
            <div className="text-[11px] font-bold text-[#FFC93A] uppercase tracking-wide">Your Team</div>
            <div className="text-[9px] text-white/40 mt-1">Product · Delivery</div>
          </div>
          <div className="flex items-center text-[#FFC93A]/50 text-lg font-thin">+</div>
          <div className="border border-white/20 bg-white/[0.03] px-4 py-3 text-center flex-1">
            <div className="text-[11px] font-bold text-white/80 uppercase tracking-wide">KIBA Engineers</div>
            <div className="text-[9px] text-white/40 mt-1">On our payroll · Pre-vetted</div>
          </div>
        </div>
        <div className="w-full grid grid-cols-3 gap-2">
          {[
            { r: "1 Engineer", t: "2–3 days" },
            { r: "Team of 3–5", t: "< 5 days" },
            { r: "10+ Engineers", t: "1–2 weeks" },
          ].map((s) => (
            <div key={s.r} className="border border-white/10 px-2 py-2.5 text-center">
              <div className="text-[12px] font-bold text-[#FFC93A]">{s.r}</div>
              <div className="text-[9px] text-white/40 mt-0.5">{s.t}</div>
            </div>
          ))}
        </div>
        <div className="text-[9px] text-[#FFC93A]/70 uppercase tracking-wider">Zero hiring overhead · Senior Tech Lead included</div>
      </div>
    ),
    offshore: (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-8 gap-3">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFC93A] font-bold">Delivery Lifecycle</div>
        <div className="w-full flex flex-col gap-0">
          {[
            { phase: "01", label: "Discovery & Architecture", sub: "Requirements · Design · Estimation", yellow: false },
            { phase: "02", label: "Build & Develop", sub: "Dedicated POD · Agile Sprints", yellow: true },
            { phase: "03", label: "QA & Assurance", sub: "85%+ Coverage · Automated Testing", yellow: false },
            { phase: "04", label: "Deploy & Handoff", sub: "Prod Release · Docs · Knowledge Transfer", yellow: false },
          ].map((p, i, arr) => (
            <React.Fragment key={p.phase}>
              <div className={`flex items-center gap-4 px-3 py-2.5 border ${p.yellow ? "border-[#FFC93A]/50 bg-[#FFC93A]/6" : "border-white/10 bg-white/[0.02]"}`}>
                <span className="text-[10px] font-mono text-[#FFC93A]/50 w-5 flex-shrink-0">{p.phase}</span>
                <div>
                  <div className={`text-[11px] font-bold uppercase tracking-wide ${p.yellow ? "text-[#FFC93A]" : "text-white/75"}`}>{p.label}</div>
                  <div className="text-[9px] text-white/35 mt-0.5">{p.sub}</div>
                </div>
              </div>
              {i < arr.length - 1 && <div className="ml-[22px] w-px h-3 bg-[#FFC93A]/20" />}
            </React.Fragment>
          ))}
        </div>
        <div className="text-[9px] text-[#FFC93A]/70 uppercase tracking-wider">Fixed Bid · T&amp;M · BOT, your choice</div>
      </div>
    ),
  };

  return (
    <div className="relative aspect-[16/9] border border-white/10 bg-[#060e1c] overflow-hidden">
      <div className="absolute inset-0 bg-dot-hero opacity-30" />
      <div className="absolute top-0 right-0 w-20 h-[2px] bg-[#FFC93A]" />
      <div className="absolute top-[2px] right-0 w-[2px] h-20 bg-[#FFC93A]" />
      {visuals[id] ?? null}
      <div className="absolute bottom-4 left-5 flex items-center gap-2.5 text-[9px] uppercase tracking-[0.28em] text-[#FFC93A]/50">
        <span className="w-5 h-px bg-[#FFC93A]/40" />{accent}
      </div>
      <div className="absolute bottom-4 right-5 text-[9px] uppercase tracking-[0.28em] text-white/15">KIBA Labs</div>
    </div>
  );
}

/* ─────────── What We Do, Sticky header + vertical snap ─────────── */
function WhatWeDo() {
  const ref = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [activeIdx, setActiveIdx] = useState(0);

  const skipSection = () => {
    if (!ref.current) return;
    const bottom = ref.current.getBoundingClientRect().bottom + window.scrollY;
    window.scrollTo({ top: bottom, behavior: "smooth" });
  };

  // Map scroll progress → card index with snapping
  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (v) => {
      // Reserve first ~10% of scroll for "intro pause" before advancing cards
      const adjusted = Math.max(0, (v - 0.05) / 0.95);
      const raw = adjusted * (CAPABILITY_CARDS.length - 1);
      const snapped = Math.round(raw);
      setActiveIdx(Math.max(0, Math.min(CAPABILITY_CARDS.length - 1, snapped)));
    });
    return unsubscribe;
  }, [scrollYProgress]);

  const card = CAPABILITY_CARDS[activeIdx];

  return (
    <section
      ref={ref}
      className="relative bg-[#0A1628] text-white"
      style={{ height: `${(CAPABILITY_CARDS.length + 1.5) * 100}vh` }}
      data-testid="what-we-do"
    >
      <div className="sticky top-0 h-screen flex flex-col overflow-hidden">

        {/* ── Static header band ── */}
        <div className="flex-shrink-0 border-b border-white/10 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-12 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-7">
              <Eyebrow dark>What we do</Eyebrow>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
                Your stack. Your model.{" "}
                <span className="text-[#FFC93A]">Our engineers.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-base lg:text-lg text-white/75 leading-relaxed">
                SAP to GenAI. Staff augmentation to offshore delivery. Java to Solidity. We work across every tier of enterprise technology and every engagement model, no artificial limits.
              </p>
            </div>
            <div className="lg:col-span-1 flex lg:justify-end items-end">
              <button
                onClick={skipSection}
                className="group flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/35 hover:text-white/70 transition-colors whitespace-nowrap"
                title="Skip to next section"
              >
                Skip
                <svg className="w-4 h-4 transition-transform group-hover:translate-y-0.5" fill="none" viewBox="0 0 16 16">
                  <path d="M8 3v7M4 7l4 4 4-4M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              </div>
            </div>
          </div>

        {/* ── Active capability card ── */}
        <div className="flex-1 overflow-hidden relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24, transition: { duration: 0.28 } }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 flex items-center"
            >
              <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">

                {/* Text block */}
                <div className="lg:col-span-6 xl:col-span-5">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-[#FFC93A] font-medium mb-3">
                    {card.eyebrow} · {String(activeIdx + 1).padStart(2, "0")} / {String(CAPABILITY_CARDS.length).padStart(2, "0")}
                    </div>
                    <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tightest leading-[1.02]">
                    {card.title}
                    </h3>
                  <p className="mt-6 text-base lg:text-lg text-white/80 leading-relaxed max-w-lg">
                    {card.description}
                  </p>
                    <div className="mt-8 flex flex-wrap gap-2">
                    {card.tags.map((t) => (
                      <span
                        key={t}
                        className="px-3 py-1.5 border border-white/15 text-[11px] uppercase tracking-wider text-white/75 hover:border-[#FFC93A]/60 hover:text-white transition-colors"
                      >
                        {t}
                        </span>
                      ))}
                    </div>
                    <Link
                      to="/services"
                    className="mt-10 inline-flex items-center gap-2 font-semibold text-[#FFC93A] hover:text-white transition-colors text-sm"
                    >
                    Explore all services <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>

                {/* Visual block */}
                <div className="lg:col-span-6 xl:col-span-7 hidden lg:block">
                  <CardVisual id={card.id} accent={card.accent} />
                      </div>

                      </div>
            </motion.div>
          </AnimatePresence>
                    </div>

        {/* ── Progress / navigation dots ── */}
        <div className="flex-shrink-0 border-t border-white/8 bg-[#0A1628]/95 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center gap-4 justify-between">
            <div className="flex items-center gap-2">
              {CAPABILITY_CARDS.map((c, i) => (
                <div
                  key={c.id}
                  className={`h-[3px] rounded-none transition-all duration-400 ${
                    i === activeIdx
                      ? "w-10 bg-[#FFC93A]"
                      : i < activeIdx
                      ? "w-4 bg-white/30"
                      : "w-4 bg-white/12"
                  }`}
                />
              ))}
                  </div>
            <div className="hidden sm:flex items-center gap-6">
              {CAPABILITY_CARDS.map((c, i) => (
                <span
                  key={c.id}
                  className={`text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    i === activeIdx ? "text-[#FFC93A]" : "text-white/25"
                  }`}
                >
                  {c.id === "staff-aug" ? "Staff Aug" : c.id === "offshore" ? "Offshore" : c.title.split(" ")[0]}
                </span>
              ))}
                </div>
            <div className="flex items-center gap-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-3">
                <span className="w-6 h-px bg-[#FFC93A]/50" /> Scroll to explore
              </div>
              <button
                onClick={skipSection}
                className="group flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/30 hover:text-[#FFC93A] transition-colors"
              >
                Skip section
                <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" fill="none" viewBox="0 0 16 16">
                  <path d="M8 3v7M4 7l4 4 4-4M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────── Per-industry visual config ─────────── */
const IND_META: Record<string, { accent: string; gradFrom: string; gradTo: string; stat1: string; stat1Label: string; stat2: string; stat2Label: string }> = {
  healthcare:     { accent: "#34d399", gradFrom: "#052e16", gradTo: "#0a1628", stat1: "100%", stat1Label: "Audit-ready",  stat2: "AI + Blockchain", stat2Label: "Core stack" },
  "supply-chain": { accent: "#fbbf24", gradFrom: "#1c1007", gradTo: "#0a1628", stat1: "200+",  stat1Label: "Hospitals served", stat2: "1M+", stat2Label: "Interactions / day" },
  fintech:        { accent: "#60a5fa", gradFrom: "#0a1929", gradTo: "#0a1628", stat1: "<6 mo", stat1Label: "Exchange launch", stat2: "Kafka + L2", stat2Label: "Real-time infra" },
  insurance:      { accent: "#a78bfa", gradFrom: "#120826", gradTo: "#0a1628", stat1: "+22%",  stat1Label: "Conversion lift", stat2: "Multilingual", stat2Label: "AI assistant" },
  government:     { accent: "#94a3b8", gradFrom: "#0c1118", gradTo: "#0a1628", stat1: "250k+", stat1Label: "Licences / year", stat2: "Zero-downtime", stat2Label: "Citizen services" },
  crypto:         { accent: "#c084fc", gradFrom: "#130720", gradTo: "#0a1628", stat1: "80k+",  stat1Label: "NFT card holders", stat2: "EVM + Solana", stat2Label: "Multi-chain" },
};

/* ─────────── Per-industry SVG illustrations ─────────── */
const IND_ILLUSTRATIONS: Record<string, (accent: string) => React.ReactNode> = {
  healthcare: (c) => (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* DNA helix */}
      <path d="M30 10 Q60 30 90 10" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.8"/>
      <path d="M30 30 Q60 50 90 30" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
      <path d="M30 50 Q60 70 90 50" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      <path d="M30 70 Q60 90 90 70" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
      <line x1="30" y1="10" x2="30" y2="70" stroke={c} strokeWidth="1" opacity="0.3"/>
      <line x1="90" y1="10" x2="90" y2="70" stroke={c} strokeWidth="1" opacity="0.3"/>
      <circle cx="30" cy="10" r="3" fill={c} opacity="0.9"/>
      <circle cx="90" cy="10" r="3" fill={c} opacity="0.9"/>
      <circle cx="60" cy="30" r="2.5" fill={c} opacity="0.7"/>
      <circle cx="30" cy="50" r="3" fill={c} opacity="0.8"/>
      <circle cx="90" cy="50" r="3" fill={c} opacity="0.8"/>
      <circle cx="60" cy="70" r="2.5" fill={c} opacity="0.6"/>
      {/* Cross symbol */}
      <rect x="50" y="84" width="20" height="6" rx="1" fill={c} opacity="0.7"/>
      <rect x="56" y="78" width="8" height="18" rx="1" fill={c} opacity="0.7"/>
      {/* Circuit dots */}
      <circle cx="15" cy="40" r="2" fill={c} opacity="0.3"/>
      <circle cx="105" cy="60" r="2" fill={c} opacity="0.3"/>
      <line x1="15" y1="40" x2="30" y2="50" stroke={c} strokeWidth="0.8" opacity="0.2"/>
      <line x1="105" y1="60" x2="90" y2="50" stroke={c} strokeWidth="0.8" opacity="0.2"/>
    </svg>
  ),
  "supply-chain": (c) => (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Warehouse boxes */}
      <rect x="8" y="55" width="28" height="22" rx="1" stroke={c} strokeWidth="1.5" opacity="0.85"/>
      <polyline points="8,55 22,45 36,55" stroke={c} strokeWidth="1.5" strokeLinejoin="round" opacity="0.85"/>
      <line x1="22" y1="45" x2="22" y2="42" stroke={c} strokeWidth="1.5" opacity="0.7"/>
      <rect x="46" y="48" width="28" height="29" rx="1" stroke={c} strokeWidth="1.5" opacity="0.9"/>
      <polyline points="46,48 60,36 74,48" stroke={c} strokeWidth="1.5" strokeLinejoin="round" opacity="0.9"/>
      <line x1="60" y1="36" x2="60" y2="32" stroke={c} strokeWidth="1.5" opacity="0.8"/>
      <rect x="84" y="55" width="28" height="22" rx="1" stroke={c} strokeWidth="1.5" opacity="0.85"/>
      <polyline points="84,55 98,45 112,55" stroke={c} strokeWidth="1.5" strokeLinejoin="round" opacity="0.85"/>
      <line x1="98" y1="45" x2="98" y2="42" stroke={c} strokeWidth="1.5" opacity="0.7"/>
      {/* Connecting arrows */}
      <line x1="36" y1="66" x2="46" y2="66" stroke={c} strokeWidth="1.2" strokeDasharray="2 2" opacity="0.6"/>
      <polygon points="44,63 48,66 44,69" fill={c} opacity="0.6"/>
      <line x1="74" y1="66" x2="84" y2="66" stroke={c} strokeWidth="1.2" strokeDasharray="2 2" opacity="0.6"/>
      <polygon points="82,63 86,66 82,69" fill={c} opacity="0.6"/>
      {/* Door details */}
      <rect x="18" y="66" width="8" height="11" rx="0.5" stroke={c} strokeWidth="1" opacity="0.5"/>
      <rect x="56" y="59" width="8" height="18" rx="0.5" stroke={c} strokeWidth="1" opacity="0.5"/>
      <rect x="94" y="66" width="8" height="11" rx="0.5" stroke={c} strokeWidth="1" opacity="0.5"/>
      {/* Ground line */}
      <line x1="5" y1="77" x2="115" y2="77" stroke={c} strokeWidth="0.8" opacity="0.3"/>
      {/* Nodes below */}
      <circle cx="22" cy="90" r="3" fill={c} opacity="0.5"/>
      <circle cx="60" cy="92" r="3" fill={c} opacity="0.5"/>
      <circle cx="98" cy="90" r="3" fill={c} opacity="0.5"/>
      <line x1="22" y1="90" x2="60" y2="92" stroke={c} strokeWidth="0.8" opacity="0.3"/>
      <line x1="60" y1="92" x2="98" y2="90" stroke={c} strokeWidth="0.8" opacity="0.3"/>
    </svg>
  ),
  fintech: (c) => (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Candlestick chart */}
      <line x1="20" y1="30" x2="20" y2="85" stroke={c} strokeWidth="0.8" opacity="0.2"/>
      <line x1="10" y1="85" x2="110" y2="85" stroke={c} strokeWidth="0.8" opacity="0.2"/>
      {/* Candles */}
      <line x1="30" y1="52" x2="30" y2="72" stroke={c} strokeWidth="1" opacity="0.6"/>
      <rect x="26" y="58" width="8" height="10" fill={c} opacity="0.7"/>
      <line x1="48" y1="44" x2="48" y2="68" stroke={c} strokeWidth="1" opacity="0.6"/>
      <rect x="44" y="49" width="8" height="14" stroke={c} strokeWidth="1.2" opacity="0.5"/>
      <line x1="66" y1="38" x2="66" y2="60" stroke={c} strokeWidth="1" opacity="0.7"/>
      <rect x="62" y="42" width="8" height="12" fill={c} opacity="0.8"/>
      <line x1="84" y1="30" x2="84" y2="55" stroke={c} strokeWidth="1" opacity="0.7"/>
      <rect x="80" y="34" width="8" height="16" fill={c} opacity="0.9"/>
      {/* Trend line */}
      <polyline points="26,68 44,58 62,50 80,40" stroke={c} strokeWidth="1.5" strokeDasharray="3 2" opacity="0.6"/>
      <circle cx="80" cy="40" r="3" fill={c} opacity="0.9"/>
      {/* Coin */}
      <circle cx="96" cy="32" r="12" stroke={c} strokeWidth="1.5" opacity="0.7"/>
      <text x="96" y="37" textAnchor="middle" fontSize="11" fill={c} opacity="0.9" fontWeight="bold">$</text>
      {/* Up arrow */}
      <polyline points="104,56 110,48 116,56" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.6"/>
      <line x1="110" y1="48" x2="110" y2="70" stroke={c} strokeWidth="1.5" opacity="0.6"/>
    </svg>
  ),
  insurance: (c) => (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Outer shield */}
      <path d="M60 12 L95 26 L95 58 Q95 88 60 105 Q25 88 25 58 L25 26 Z" stroke={c} strokeWidth="1.5" opacity="0.9" fill={c} fillOpacity="0.04"/>
      {/* Middle shield */}
      <path d="M60 22 L84 33 L84 58 Q84 78 60 91 Q36 78 36 58 L36 33 Z" stroke={c} strokeWidth="1" opacity="0.5" fill="none"/>
      {/* Inner shield */}
      <path d="M60 32 L74 40 L74 58 Q74 70 60 78 Q46 70 46 58 L46 40 Z" stroke={c} strokeWidth="1" opacity="0.3" fill="none"/>
      {/* Checkmark */}
      <polyline points="48,57 57,66 74,46" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.95"/>
      {/* Floating dots */}
      <circle cx="15" cy="30" r="2.5" fill={c} opacity="0.3"/>
      <circle cx="105" cy="30" r="2.5" fill={c} opacity="0.3"/>
      <circle cx="10" cy="65" r="2" fill={c} opacity="0.2"/>
      <circle cx="110" cy="65" r="2" fill={c} opacity="0.2"/>
      <line x1="15" y1="30" x2="25" y2="38" stroke={c} strokeWidth="0.8" opacity="0.2"/>
      <line x1="105" y1="30" x2="95" y2="38" stroke={c} strokeWidth="0.8" opacity="0.2"/>
    </svg>
  ),
  government: (c) => (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Dome */}
      <path d="M60 18 Q88 18 88 42 L32 42 Q32 18 60 18 Z" stroke={c} strokeWidth="1.5" opacity="0.85" fill={c} fillOpacity="0.05"/>
      <line x1="60" y1="18" x2="60" y2="12" stroke={c} strokeWidth="1.5" opacity="0.7"/>
      <circle cx="60" cy="10" r="3" fill={c} opacity="0.8"/>
      {/* Entablature */}
      <rect x="28" y="42" width="64" height="6" stroke={c} strokeWidth="1.2" opacity="0.8" fill={c} fillOpacity="0.05"/>
      {/* Columns */}
      {[38, 50, 62, 74, 86].map((x) => (
        <rect key={x} x={x - 2.5} y="48" width="5" height="30" stroke={c} strokeWidth="1" opacity="0.7" fill={c} fillOpacity="0.04"/>
      ))}
      {/* Base steps */}
      <rect x="24" y="78" width="72" height="5" stroke={c} strokeWidth="1" opacity="0.7" fill={c} fillOpacity="0.04"/>
      <rect x="18" y="83" width="84" height="5" stroke={c} strokeWidth="1" opacity="0.6" fill={c} fillOpacity="0.03"/>
      <rect x="12" y="88" width="96" height="5" stroke={c} strokeWidth="1" opacity="0.5" fill={c} fillOpacity="0.03"/>
      {/* Stars */}
      <circle cx="20" cy="25" r="2" fill={c} opacity="0.3"/>
      <circle cx="100" cy="25" r="2" fill={c} opacity="0.3"/>
      <circle cx="15" cy="60" r="1.5" fill={c} opacity="0.2"/>
      <circle cx="105" cy="60" r="1.5" fill={c} opacity="0.2"/>
    </svg>
  ),
  crypto: (c) => (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Hexagon chain */}
      <polygon points="30,28 40,22 50,28 50,40 40,46 30,40" stroke={c} strokeWidth="1.5" opacity="0.9" fill={c} fillOpacity="0.06"/>
      <polygon points="55,28 65,22 75,28 75,40 65,46 55,40" stroke={c} strokeWidth="1.5" opacity="0.85" fill={c} fillOpacity="0.08"/>
      <polygon points="80,28 90,22 100,28 100,40 90,46 80,40" stroke={c} strokeWidth="1.5" opacity="0.8" fill={c} fillOpacity="0.06"/>
      {/* Chain links between hex */}
      <line x1="50" y1="34" x2="55" y2="34" stroke={c} strokeWidth="2" opacity="0.7"/>
      <line x1="75" y1="34" x2="80" y2="34" stroke={c} strokeWidth="2" opacity="0.7"/>
      {/* Center coin, large */}
      <circle cx="60" cy="75" r="28" stroke={c} strokeWidth="1.5" opacity="0.8" fill={c} fillOpacity="0.05"/>
      <circle cx="60" cy="75" r="22" stroke={c} strokeWidth="0.8" opacity="0.4" fill="none"/>
      {/* Bitcoin B */}
      <path d="M53 64 L53 86 M53 64 L62 64 Q68 64 68 70 Q68 75 62 75 L53 75 M53 75 L63 75 Q70 75 70 81 Q70 87 63 87 L53 87" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"/>
      <line x1="51" y1="62" x2="51" y2="88" stroke={c} strokeWidth="1" opacity="0.5"/>
      <line x1="69" y1="62" x2="69" y2="88" stroke={c} strokeWidth="1" opacity="0.5"/>
      {/* Network dots */}
      <circle cx="15" cy="75" r="3" fill={c} opacity="0.4"/>
      <circle cx="105" cy="75" r="3" fill={c} opacity="0.4"/>
      <line x1="15" y1="75" x2="32" y2="75" stroke={c} strokeWidth="1" strokeDasharray="2 2" opacity="0.3"/>
      <line x1="88" y1="75" x2="105" y2="75" stroke={c} strokeWidth="1" strokeDasharray="2 2" opacity="0.3"/>
      <circle cx="15" cy="100" r="2" fill={c} opacity="0.3"/>
      <circle cx="105" cy="100" r="2" fill={c} opacity="0.3"/>
      <line x1="15" y1="100" x2="105" y2="100" stroke={c} strokeWidth="0.8" strokeDasharray="3 3" opacity="0.2"/>
    </svg>
  ),
};

/* ─────────── Industry illustrated panel ─────────── */
function IndustryPanel({ slug, isActive }: { slug: string; isActive: boolean }) {
  const m = IND_META[slug] ?? { accent: "#FFC93A", gradFrom: "#0a1628", gradTo: "#060e1c", stat1: ", ", stat1Label: "", stat2: ", ", stat2Label: "" };
  const illustration = IND_ILLUSTRATIONS[slug]?.(m.accent);
  return (
    <div
      className="absolute inset-0 transition-all duration-700"
      style={{ background: `linear-gradient(145deg, ${m.gradFrom} 0%, ${m.gradTo} 100%)` }}
    >
      {/* Dot grid texture */}
      <div className="absolute inset-0 bg-dot-hero opacity-20" />

      {/* Accent glow blobs */}
      <div
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl transition-opacity duration-700"
        style={{ background: m.accent, opacity: isActive ? 0.22 : 0.1 }}
      />
      <div
        className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full blur-2xl transition-opacity duration-700"
        style={{ background: m.accent, opacity: isActive ? 0.12 : 0.05 }}
      />

      {/* Accent corner mark */}
      <div className="absolute top-0 left-0 w-8 h-[2px]" style={{ background: m.accent }} />
      <div className="absolute top-0 left-0 w-[2px] h-8" style={{ background: m.accent }} />

      {/* Illustration, centred, fades slightly when active */}
      <div
        className="absolute inset-0 flex items-center justify-center transition-opacity duration-700 p-10"
        style={{ opacity: isActive ? 0.35 : 0.85 }}
      >
        {illustration}
      </div>

      {/* Stats strip, bottom, hidden when active */}
      <div
        className="absolute bottom-0 left-0 right-0 px-4 pb-4 transition-opacity duration-300"
        style={{ opacity: isActive ? 0 : 1 }}
      >
        <div className="flex gap-4 border-t pt-3" style={{ borderColor: m.accent + "33" }}>
          <div>
            <div className="text-[13px] font-extrabold leading-none" style={{ color: m.accent }}>{m.stat1}</div>
            <div className="text-[9px] uppercase tracking-wider text-white/40 mt-0.5">{m.stat1Label}</div>
          </div>
          <div className="w-px self-stretch" style={{ background: m.accent + "30" }} />
          <div>
            <div className="text-[13px] font-extrabold leading-none text-white/75">{m.stat2}</div>
            <div className="text-[9px] uppercase tracking-wider text-white/40 mt-0.5">{m.stat2Label}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Industries accordion section ─────────── */
function IndustriesAccordionSection() {
  // Desktop hover accordion
  const [hovered, setHovered] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMouseEnter = (i: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setHovered(i), 500);
  };
  const handleMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setHovered(null);
  };

  const sharedHeader = (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
      <SectionReveal className="mb-10 lg:mb-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-5">
          <Eyebrow dark>Industry Expertise</Eyebrow>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Deep domain knowledge.<br />
            <span className="text-[#FFC93A]">Production-grade delivery.</span>
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 flex items-end">
          <p className="text-base sm:text-lg text-white/75 leading-relaxed">
            From pharmaceutical serialisation to on-chain DeFi, we've shipped regulated, audit-ready systems across six industries where getting it wrong is never an option.
          </p>
        </div>
      </SectionReveal>
    </div>
  );

  return (
    <section className="bg-[#060e1c] py-16 sm:py-20 lg:py-28 text-white" data-testid="industries-strip">

      {sharedHeader}

      {/* ── Desktop (≥ 1024px): hover-expand flex accordion ── */}
      <div
        className="hidden lg:flex h-[560px] gap-[2px]"
        onMouseLeave={handleMouseLeave}
      >
        {INDUSTRIES.map((ind, i) => {
          const isActive = hovered === i;
          const isDimmed = hovered !== null && !isActive;
          const Icon = ind.Icon;
          return (
            <div
              key={ind.slug}
              onMouseEnter={() => handleMouseEnter(i)}
              data-testid={`industry-tile-${ind.slug}`}
              style={{ flex: isActive ? 4 : isDimmed ? 0.55 : 1, transition: "flex 0.65s cubic-bezier(0.22,1,0.36,1)" }}
              className="relative overflow-hidden min-w-0 cursor-pointer bg-[#0A1628]"
            >
              <IndustryPanel slug={ind.slug} isActive={isActive} />
              {/* Reading scrim */}
              <div className="absolute inset-0 transition-opacity duration-700 pointer-events-none" style={{ opacity: isActive ? 1 : 0 }}>
                <div className="absolute inset-0 bg-gradient-to-r from-[#060e1c]/80 via-[#060e1c]/20 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060e1c]/60 via-transparent to-transparent" />
              </div>
              {/* Yellow left bar */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#FFC93A] transition-opacity duration-500" style={{ opacity: isActive ? 1 : 0 }} />
              {/* Collapsed vertical label */}
              <div className="absolute top-12 left-0 flex flex-col items-center gap-3 px-3 transition-opacity duration-300" style={{ opacity: isActive ? 0 : isDimmed ? 0.6 : 1 }}>
                <span className="text-[10px] tabular-nums text-white/30 tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</span>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/75 whitespace-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>{ind.title}</div>
              </div>
              {/* Expanded content */}
              <div className="absolute inset-0 p-8 flex flex-col justify-between transition-opacity duration-500" style={{ opacity: isActive ? 1 : 0, pointerEvents: isActive ? "auto" : "none" }}>
                <div className="flex items-start justify-between">
                  <span className="text-[11px] tabular-nums text-white/40 tracking-[0.2em]">{String(i + 1).padStart(2, "0")} / {String(INDUSTRIES.length).padStart(2, "0")}</span>
                  <Icon className="w-7 h-7 text-[#FFC93A]" strokeWidth={1.2} />
                </div>
                <div className="text-left">
                  <h3 className="font-display text-3xl lg:text-4xl font-extrabold text-white leading-tight tracking-tight">{ind.title}</h3>
                  <p className="mt-3 text-sm text-white/75 leading-relaxed max-w-sm">{ind.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {ind.highlights.slice(0, 3).map((h, hi) => (
                      <li key={hi} className="flex items-start gap-2 text-[12px] text-white/70 leading-snug">
                        <span className="w-[5px] h-[5px] rounded-full bg-[#FFC93A] flex-shrink-0 mt-1.5" />
                        <span className="max-w-[340px]">{h.length > 90 ? h.slice(0, 88) + "…" : h}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/industries" className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#FFC93A] font-semibold hover:text-white transition-colors">
                    Explore this vertical <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
              </div>
            );
          })}
      </div>

      {/* ── Tablet (640–1023px): 2-column always-visible grid ── */}
      <div className="hidden sm:grid lg:hidden grid-cols-2 gap-[2px]">
        {INDUSTRIES.map((ind, i) => {
          const Icon = ind.Icon;
          return (
            <div key={ind.slug} className="relative overflow-hidden bg-[#0A1628] h-[300px]">
              <IndustryPanel slug={ind.slug} isActive={true} />
              {/* Reading scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#060e1c]/90 via-[#060e1c]/40 to-transparent" />
              {/* Yellow left bar */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#FFC93A]" />
              <div className="absolute inset-0 p-5 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tabular-nums text-white/35 tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</span>
                  <Icon className="w-5 h-5 text-[#FFC93A]" strokeWidth={1.4} />
                </div>
                <div>
                  <h3 className="font-display text-lg sm:text-xl font-extrabold text-white leading-tight">{ind.title}</h3>
                  <p className="mt-2 text-[12px] text-white/75 leading-relaxed line-clamp-2">{ind.desc}</p>
                  <ul className="mt-3 space-y-1.5">
                    {ind.highlights.slice(0, 2).map((h, hi) => (
                      <li key={hi} className="flex items-start gap-2 text-[11px] text-white/80 leading-snug">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FFC93A] flex-shrink-0 mt-1" />
                        <span className="line-clamp-1">{h}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/industries" className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#FFC93A] font-semibold">
                    Explore <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Mobile (< 640px): single-column always-visible cards ── */}
      <div className="sm:hidden flex flex-col gap-[2px]">
        {INDUSTRIES.map((ind, i) => {
          const Icon = ind.Icon;
          return (
            <div key={ind.slug} className="relative overflow-hidden bg-[#0A1628] h-[260px]">
              <IndustryPanel slug={ind.slug} isActive={true} />
              {/* Gradient scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#060e1c]/92 via-[#060e1c]/50 to-transparent" />
              {/* Yellow left bar */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#FFC93A]" />
              <div className="absolute inset-0 px-5 py-4 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tabular-nums text-white/30 tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                  <Icon className="w-4 h-4 text-[#FFC93A]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-display text-xl font-extrabold text-white leading-tight">{ind.title}</h3>
                  <p className="mt-2 text-sm text-white/75 leading-relaxed line-clamp-2">{ind.desc}</p>
                  <ul className="mt-3 space-y-1.5">
                    {ind.highlights.slice(0, 2).map((h, hi) => (
                      <li key={hi} className="flex items-start gap-2 text-[12px] text-white/80 leading-snug">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FFC93A] flex-shrink-0 mt-1.5" />
                        <span className="line-clamp-1">{h}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/industries" className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#FFC93A] font-semibold">
                    Explore this vertical <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </section>
  );
}

/* ─────────── Capability Highlights data ─────────── */
const CAPABILITY_HIGHLIGHTS = [
  {
    id: "ai",
    category: "Applied AI · GenAI Engineering",
    headline: "Intelligence that acts, not just answers.",
    useCases: [
      "RAG copilots extracting insights from medical records, cutting review time by 45%",
      "NLP pipelines processing 1M+ daily interactions for real-time customer intelligence",
      "Multilingual LLM sales assistants lifting lead conversion by 22%",
    ],
    tags: ["RAG · LLMs", "MLOps", "NLP"],
    stat: "45%",
    statLabel: "Manual effort eliminated",
    accentColor: "#60a5fa",
    visual: (
      <svg viewBox="0 0 280 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Neural network nodes */}
        {[[40,80],[40,40],[40,120],[140,60],[140,100],[140,30],[140,130],[240,80],[240,40],[240,120]].map(([cx,cy],i) => (
          <circle key={i} cx={cx} cy={cy} r="8" fill="#60a5fa" fillOpacity={i===7?"0.9":"0.4"} />
        ))}
        {/* Connections */}
        {[[40,80,140,60],[40,80,140,100],[40,80,140,30],[40,80,140,130],[40,40,140,60],[40,40,140,30],[40,120,140,100],[40,120,140,130],[140,60,240,80],[140,60,240,40],[140,100,240,80],[140,100,240,120],[140,30,240,40],[140,130,240,120]].map(([x1,y1,x2,y2],i) => (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#60a5fa" strokeWidth="1" strokeOpacity="0.2"/>
        ))}
        {/* Active path highlight */}
        <line x1="40" y1="80" x2="140" y2="60" stroke="#60a5fa" strokeWidth="2" strokeOpacity="0.8"/>
        <line x1="140" y1="60" x2="240" y2="80" stroke="#60a5fa" strokeWidth="2" strokeOpacity="0.8"/>
        {/* Labels */}
        <text x="140" y="155" textAnchor="middle" fontSize="9" fill="#60a5fa" fillOpacity="0.5" letterSpacing="2" fontFamily="monospace">INPUT → REASONING → OUTPUT</text>
        {/* Pulse ring on output */}
        <circle cx="240" cy="80" r="14" stroke="#60a5fa" strokeWidth="1" strokeOpacity="0.3" fill="none"/>
        <circle cx="240" cy="80" r="20" stroke="#60a5fa" strokeWidth="0.5" strokeOpacity="0.15" fill="none"/>
      </svg>
    ),
  },
  {
    id: "sap",
    category: "SAP Engineering · BTP · ABAP",
    headline: "Enterprise SAP, re-engineered for the cloud.",
    useCases: [
      "Cloud-native BTP apps with CAP integrating S/4HANA across multi-country operations",
      "ABAP RAP extensions & Fiori UX, zero base-code changes, clean upgrades",
      "CPI integration flows connecting SAP to third-party APIs and legacy ERPs",
    ],
    tags: ["BTP · CAP", "ABAP · RAP", "CPI · Fiori"],
    stat: "60%",
    statLabel: "Integration overhead cut",
    accentColor: "#34d399",
    visual: (
      <svg viewBox="0 0 280 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Architecture boxes */}
        <rect x="10" y="55" width="60" height="30" rx="2" stroke="#34d399" strokeWidth="1.5" strokeOpacity="0.7" fill="#34d399" fillOpacity="0.05"/>
        <text x="40" y="74" textAnchor="middle" fontSize="8" fill="#34d399" fillOpacity="0.8" fontFamily="monospace">S/4HANA</text>
        <rect x="110" y="30" width="60" height="25" rx="2" stroke="#34d399" strokeWidth="1.5" strokeOpacity="0.8" fill="#34d399" fillOpacity="0.08"/>
        <text x="140" y="46" textAnchor="middle" fontSize="8" fill="#34d399" fillOpacity="0.9" fontFamily="monospace">BTP</text>
        <rect x="110" y="65" width="60" height="25" rx="2" stroke="#34d399" strokeWidth="1.2" strokeOpacity="0.6" fill="#34d399" fillOpacity="0.05"/>
        <text x="140" y="81" textAnchor="middle" fontSize="8" fill="#34d399" fillOpacity="0.7" fontFamily="monospace">CPI</text>
        <rect x="110" y="100" width="60" height="25" rx="2" stroke="#34d399" strokeWidth="1.2" strokeOpacity="0.6" fill="#34d399" fillOpacity="0.05"/>
        <text x="140" y="116" textAnchor="middle" fontSize="8" fill="#34d399" fillOpacity="0.7" fontFamily="monospace">CAP / RAP</text>
        <rect x="210" y="30" width="60" height="25" rx="2" stroke="#34d399" strokeWidth="1" strokeOpacity="0.5" fill="#34d399" fillOpacity="0.04"/>
        <text x="240" y="46" textAnchor="middle" fontSize="8" fill="#34d399" fillOpacity="0.6" fontFamily="monospace">Fiori UX</text>
        <rect x="210" y="65" width="60" height="25" rx="2" stroke="#34d399" strokeWidth="1" strokeOpacity="0.5" fill="#34d399" fillOpacity="0.04"/>
        <text x="240" y="81" textAnchor="middle" fontSize="8" fill="#34d399" fillOpacity="0.6" fontFamily="monospace">APIs</text>
        <rect x="210" y="100" width="60" height="25" rx="2" stroke="#34d399" strokeWidth="1" strokeOpacity="0.5" fill="#34d399" fillOpacity="0.04"/>
        <text x="240" y="116" textAnchor="middle" fontSize="8" fill="#34d399" fillOpacity="0.6" fontFamily="monospace">Third-party</text>
        {/* Arrows */}
        <line x1="70" y1="70" x2="110" y2="43" stroke="#34d399" strokeWidth="1.2" strokeOpacity="0.5"/>
        <line x1="70" y1="70" x2="110" y2="77" stroke="#34d399" strokeWidth="1.2" strokeOpacity="0.5"/>
        <line x1="70" y1="70" x2="110" y2="112" stroke="#34d399" strokeWidth="1" strokeOpacity="0.4"/>
        <line x1="170" y1="43" x2="210" y2="43" stroke="#34d399" strokeWidth="1.2" strokeOpacity="0.6"/>
        <line x1="170" y1="77" x2="210" y2="77" stroke="#34d399" strokeWidth="1.2" strokeOpacity="0.6"/>
        <line x1="170" y1="112" x2="210" y2="112" stroke="#34d399" strokeWidth="1" strokeOpacity="0.5"/>
        {/* SAP label */}
        <text x="140" y="152" textAnchor="middle" fontSize="9" fill="#34d399" fillOpacity="0.4" letterSpacing="2" fontFamily="monospace">ENTERPRISE INTEGRATION STACK</text>
      </svg>
    ),
  },
  {
    id: "lifescience",
    category: "Life Sciences · Supply Chain",
    headline: "Drug safety from factory to patient.",
    useCases: [
      "Pharma serialisation across 18+ sites, cryptographic track-and-trace per unit",
      "Private blockchain networks authenticating every drug transaction at distribution scale",
      "National warehousing platform dispatching to 200+ hospitals with full audit trails",
    ],
    tags: ["Serialisation", "Blockchain", "Supply Chain"],
    stat: "200+",
    statLabel: "Hospitals on live platform",
    accentColor: "#fbbf24",
    visual: (
      <svg viewBox="0 0 280 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Header */}
        <text x="140" y="16" textAnchor="middle" fontSize="7" fill="#fbbf24" fillOpacity="0.7" fontFamily="monospace" letterSpacing="1.5">SUPPLY CHAIN · TRACK &amp; TRACE</text>

        {/* 5 nodes: box width=48, gap=8, starts at x=4. Centers: 28,84,140,196,252 */}
        {[
          { label: "Mfg",       x: 4   },
          { label: "QA",        x: 60  },
          { label: "Warehouse", x: 116 },
          { label: "Logistics", x: 172 },
          { label: "Hospital",  x: 228 },
        ].map((node, i) => (
          <g key={node.label}>
            {/* Box */}
            <rect
              x={node.x} y="26" width="48" height="28" rx="2"
              stroke="#fbbf24"
              strokeWidth={i === 2 ? "1.8" : "1"}
              strokeOpacity={i === 2 ? "1" : "0.55"}
              fill="#fbbf24"
              fillOpacity={i === 2 ? "0.14" : "0.04"}
            />
            {/* Label */}
            <text
              x={node.x + 24} y="44"
              textAnchor="middle" fontSize="7" fill="#fbbf24"
              fillOpacity={i === 2 ? "1" : "0.75"}
              fontFamily="monospace"
            >
              {node.label}
            </text>
            {/* Arrow to next box (gap=8px, arrowhead 4px) */}
            {i < 4 && (
              <>
                <line
                  x1={node.x + 48} y1="40"
                  x2={node.x + 54} y2="40"
                  stroke="#fbbf24" strokeWidth="1" strokeOpacity="0.5"
                />
                <polygon
                  points={`${node.x + 54},37 ${node.x + 58},40 ${node.x + 54},43`}
                  fill="#fbbf24" fillOpacity="0.55"
                />
              </>
            )}
          </g>
        ))}

        {/* Divider */}
        <line x1="4" y1="64" x2="276" y2="64" stroke="#fbbf24" strokeWidth="0.5" strokeOpacity="0.2"/>

        {/* Three QR codes */}
        {[72, 130, 188].map((x) => (
          <g key={x}>
            <rect x={x}     y="72" width="12" height="12" rx="1" stroke="#fbbf24" strokeWidth="0.8" strokeOpacity="0.55" fill="none"/>
            <rect x={x+1.5} y="73.5" width="3.5" height="3.5" fill="#fbbf24" fillOpacity="0.55"/>
            <rect x={x+7}   y="73.5" width="3.5" height="3.5" fill="#fbbf24" fillOpacity="0.55"/>
            <rect x={x+1.5} y="79"   width="3.5" height="3.5" fill="#fbbf24" fillOpacity="0.55"/>
            <rect x={x+7}   y="79"   width="2"   height="2"   fill="#fbbf24" fillOpacity="0.4"/>
          </g>
        ))}

        {/* Audit labels */}
        <text x="140" y="96" textAnchor="middle" fontSize="7" fill="#fbbf24" fillOpacity="0.75" fontFamily="monospace" letterSpacing="1">
          SERIALISED · AUTHENTICATED · AUDITED
        </text>

        {/* Blockchain blocks */}
        {[18, 80, 142, 204].map((x, i) => (
          <g key={x}>
            <rect x={x} y="106" width="44" height="16" rx="1"
              stroke="#fbbf24" strokeWidth="0.8" strokeOpacity="0.45"
              fill="#fbbf24" fillOpacity="0.05"/>
            <text x={x + 22} y="118" textAnchor="middle" fontSize="6"
              fill="#fbbf24" fillOpacity="0.5" fontFamily="monospace">
              {["#A3F", "#B4C", "#C5D", "#D6E"][i]}
            </text>
            {i < 3 && (
              <line x1={x+44} y1="114" x2={x+55} y2="114"
                stroke="#fbbf24" strokeWidth="0.8" strokeOpacity="0.35" strokeDasharray="2 1"/>
            )}
          </g>
        ))}

        <text x="140" y="138" textAnchor="middle" fontSize="7" fill="#fbbf24" fillOpacity="0.65" letterSpacing="1.5" fontFamily="monospace">
          ON-CHAIN AUDIT TRAIL
        </text>
      </svg>
    ),
  },
];

/* ─────────── Capability Highlights Section ─────────── */
function CapabilityHighlights() {
  return (
    <section className="bg-[#F9F6F0] py-24 lg:py-32 border-y border-[#0A1628]/8" data-testid="capability-highlights">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

        {/* Header */}
        <SectionReveal className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <Eyebrow>Capabilities in Action</Eyebrow>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-[#0A1628]">
              Multiple domains.<br />
              <span className="text-[#0A1628]">Enterprise-scale impact.</span>
            </h2>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-[#0A1628] font-semibold link-underline text-sm">
            All capabilities <ArrowRight className="w-4 h-4" />
          </Link>
        </SectionReveal>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7 items-stretch">
          {CAPABILITY_HIGHLIGHTS.map((cap, i) => (
            <SectionReveal key={cap.id} delay={i * 0.1} className="h-full">
              <Tilt tiltMaxAngleX={4} tiltMaxAngleY={4} perspective={1400} scale={1.01} transitionSpeed={900} glareEnable glareMaxOpacity={0.08} glareColor={cap.accentColor} glarePosition="all" glareBorderRadius="0px" className="h-full">
                <div className="group flex flex-col h-full bg-[#0A1628] text-white border border-white/8 hover:border-[#FFC93A]/35 transition-colors duration-500">

                  {/* Visual panel */}
                  <div className="relative h-[220px] overflow-hidden bg-[#060e1c] flex-shrink-0">
                    <div className="absolute inset-0 bg-dot-hero opacity-25" />
                    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl" style={{ background: cap.accentColor, opacity: 0.14 }} />
                    {/* Accent corner */}
                    <div className="absolute top-0 left-0 w-10 h-[2px]" style={{ background: cap.accentColor }} />
                    <div className="absolute top-0 left-0 w-[2px] h-10" style={{ background: cap.accentColor }} />
                    <div className="absolute inset-0 p-5">{cap.visual}</div>
                    <div className="absolute top-3 right-4 font-mono text-[10px] text-white/18 tracking-widest">0{i + 1}</div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-1 p-7 lg:p-8">
                    {/* Category */}
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: cap.accentColor }}>
                      {cap.category}
                    </div>
                    {/* Headline */}
                    <h3 className="font-display text-xl sm:text-2xl font-extrabold text-white leading-snug mb-5 group-hover:text-[#FFC93A] transition-colors duration-300">
                      {cap.headline}
                    </h3>
                    {/* Use cases */}
                    <ul className="space-y-3 flex-1">
                      {cap.useCases.map((uc, j) => (
                        <li key={j} className="flex items-start gap-3 text-[13px] text-white/75 leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-[6px]" style={{ background: cap.accentColor }} />
                          {uc}
                        </li>
                      ))}
                    </ul>
                    {/* Footer: stat left, tags right */}
                    <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between gap-4">
                      <div className="flex-shrink-0">
                        <div className="font-display text-3xl font-extrabold leading-none" style={{ color: cap.accentColor }}>{cap.stat}</div>
                        <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1 whitespace-nowrap">{cap.statLabel}</div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 justify-end">
                        {cap.tags.map((t) => (
                          <span key={t} className="px-2.5 py-1 border border-white/12 text-[9px] uppercase tracking-wide text-white/70 whitespace-nowrap">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                </div>
              </Tilt>
            </SectionReveal>
          ))}
        </div>

      </div>
    </section>
  );
}

function CaseThumb({ index }: { index: number }) {
  return (
    <div className="absolute inset-0 bg-[#0E1B30]">
      <div className="absolute inset-0 bg-dot-hero opacity-80" />
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#FFC93A]/25 blur-2xl rounded-full" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="font-display text-8xl font-extrabold text-white/10">
          {String(index + 1).padStart(2, "0")}
        </div>
      </div>
      <div className="absolute left-0 right-0 bottom-0 h-1 bg-[#FFC93A]" />
    </div>
  );
}

/* ─────────── Industry Tile Video ─────────── */
function IndustryTileVideo({ slug }: { slug: string }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const vid = ref.current;
    if (!vid) return;
    const tryPlay = () => { const p = vid.play(); if (p && p.catch) p.catch(() => {}); };
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) tryPlay();
          else vid.pause();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(vid);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-[1.2s] ease-out group-hover:scale-110"
      aria-hidden="true"
    >
      <source src={`/videos/ind-${slug}.webm`} type="video/webm" />
      <source src={`/videos/ind-${slug}.mp4`} type="video/mp4" />
    </video>
  );
}